'use strict';

// Talks to the locally-running RD (Registered Device) biometric service to
// capture a PidData XML blob for AEPS/DMT calls. Mirrors webpayment's
// app/composables/useRdService.ts — same vendor ports, same DEVICEINFO/CAPTURE
// verbs (every UIDAI-certified RD driver exposes these two, just on its own
// port so multiple vendors' services can coexist on one machine).
const RD_VENDORS = [
  { id: 'mantra', name: 'Mantra', port: 11100 },   // MFS100 — confirmed against a live device
  { id: 'morpho', name: 'Morpho', port: 11101 },   // per vendor docs — unverified in this SDK
  { id: 'startek', name: 'Startek', port: 11103 }, // per vendor docs — unverified in this SDK
  { id: 'secugen', name: 'Secugen', port: 11102 }, // per vendor docs — unverified in this SDK
];

const PID_OPTIONS_XML = '<?xml version="1.0"?><PidOptions ver="1.0"><Opts fCount="1" fType="2" iCount="0" iType="0" pCount="0" pType="0" format="0" pidVer="2.0" timeout="10000" posh="UNKNOWN" env="P" wadh="E0jzJ/P8UopUHAieZn8CKqS4WPMi5ZSYXgfnlfkWjrc="/></PidOptions>';

function attr(xml, name) {
  const m = xml.match(new RegExp(`${name}="([^"]+)"`)) || xml.match(new RegExp(`<${name}>([^<]+)</${name}>`));
  return m ? m[1] : undefined;
}

async function probeVendor(vendor) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 4000);
    const res = await fetch(`http://127.0.0.1:${vendor.port}/rd/info`, {
      method: 'DEVICEINFO',
      headers: { 'Content-Type': 'text/xml' },
      signal: controller.signal,
    });
    clearTimeout(timer);
    if (!res.ok) return null;
    const xml = await res.text();
    const dpId = attr(xml, 'dpId');
    const rdsId = attr(xml, 'rdsId');
    if (!dpId || !rdsId) return null;
    const dc = (attr(xml, 'dc') || '').toUpperCase();
    return {
      vendor,
      dpId,
      rdsId,
      driverVersion: attr(xml, 'rdsVer') || '—',
      modelInfo: attr(xml, 'mi') || vendor.name,
      healthy: dc ? dc === 'Y' : true,
    };
  } catch (e) {
    return null;
  }
}

/** Tries each vendor's port in priority order, stopping at the first that answers. */
async function findFirstConnectedDevice() {
  for (const vendor of RD_VENDORS) {
    const info = await probeVendor(vendor);
    if (info) return info;
  }
  return null;
}

/** Captures a fingerprint from the given vendor's RD service, returns raw PidData XML. */
async function captureFingerprint(vendor) {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    const res = await fetch(`http://127.0.0.1:${vendor.port}/rd/capture`, {
      method: 'CAPTURE',
      headers: { 'Content-Type': 'text/xml' },
      body: PID_OPTIONS_XML,
      signal: controller.signal,
    });
    clearTimeout(timer);
    const xml = await res.text();
    const errCode = attr(xml, 'errCode');
    const errInfo = attr(xml, 'errInfo');
    if (errCode && errCode !== '0') throw new Error(errInfo || `Capture failed (errCode ${errCode})`);
    return { ok: true, pidDataXml: xml };
  } catch (err) {
    const msg = err && err.message || '';
    const friendly = msg.includes('Failed to fetch') || err?.name === 'AbortError'
      ? 'Lost connection to the biometric device during capture'
      : msg || 'Fingerprint capture failed — ask the customer to try again';
    return { ok: false, message: friendly };
  }
}

module.exports = { RD_VENDORS, findFirstConnectedDevice, captureFingerprint };
